<!--
// app/pages/mercato.vue
-->
<script setup lang="ts">
import type { MercatoTeam } from "~~/src/mercato/domain/models/MercatoTeam";
import type { MercatoPlayer } from "~~/src/mercato/domain/models/MercatoPlayer";
import MercatoFaqs from "~/components/pages/mercato/MercatoFaqs.vue";
import MercatoKeyConcepts from "~/components/pages/mercato/MercatoKeyConcepts.vue";
import MarketSection from "~/components/pages/mercato/MarketSection.vue";
import TeamsSection from "~/components/pages/mercato/TeamsSection.vue";
import TransfersSection from "~/components/pages/mercato/TransfersSection.vue";

definePageMeta({
  layout: "minimal",
  middleware: ["not-found-queens"],
});

const { $t, $getLocale } = useI18n();
const route = useRoute();
const locale = $getLocale();
const { deleteQuery, addQuery } = useRouteQuery();

useSeoMeta({
  title: "Mercato Kings League 2026",
});

const activeLeague = ref(route.query.league || "all");
const activeTab = ref("market");
const isSimulatorOpen = ref(false);

//static info consts
const availableLeagues = ["all", "spain", "brazil", "italy", "mexico", "germany"];

const windows = [
  { src: "/img/mercato/mercato-tl-kickoff.jpg", alt: "Mercato Kickoff 5/02" },
  { src: "/img/mercato/mercato-tl-1.jpg", alt: "Mercato Window 1 9/02" },
  { src: "/img/mercato/mercato-tl-2.jpg", alt: "Mercato Window 2 10/02" },
  { src: "/img/mercato/mercato-tl-3.jpg", alt: "Mercato Window 3 11/02" },
  { src: "/img/mercato/mercato-tl-4.jpg", alt: "Mercato Window 4 12/02" },
];

const videoLangMapping = {
  es: "sqgz8rknIhQ",
  en: "Bqk5ScL415g",
  de: "P5KCz-z88qo",
  it: "wDI_M6a4Eqo",
  pt: "aKvy40zp5Vk",
  fr: "Bqk5ScL415g",
  ar: "Bqk5ScL415g",
};

//api calls
const { data: kingsTeams, error: kingsTeamsError } = await useFetch<MercatoTeam[]>("/api/v1/mercato/teams");
if (kingsTeamsError.value) console.error(kingsTeamsError.value);

const {
  data: marketPlayers,
  error: marketPlayersError,
  status: marketPlayersStatus,
} = await useLazyAsyncData("mercatoMarketPlayers", (_nuxtApp, { signal }) =>
  $fetch<MercatoPlayer[]>("/api/v1/mercato/players", { signal })
);
if (marketPlayersError.value) console.error(marketPlayersError.value);

//utils functions
const handleLeagueFilter = (league: string) => {
  activeLeague.value = league;
  if (league === "all" && route.query.league) {
    deleteQuery("league");
  } else {
    addQuery({ league });
  }
};

const openSimulator = () => {
  isSimulatorOpen.value = true;
  window.scrollTo(0, 0);
};
</script>

<template>
  <main class="kql-mercato">
    <!-- Floating Key Concepts button -->
    <mercato-key-concepts />

    <div class="flex items-center justify-center py-6 md:py-10 header">
      <h1 class="sr-only">Kings League Mercato 2026</h1>
      <img src="/img/mercato/mercato-logo.svg" alt="Mercato Kings League 2026" class="w-1/2 md:w-1/3" />
    </div>
    <div class="flex flex-col items-center justify-center py-7 px-3">
      <div class="grid grid-cols-3 bigsm:grid-cols-6 gap-2 md:gap-4 auto-rows-fr">
        <button
          v-for="league in availableLeagues"
          type="button"
          :class="['league-filter--button', { active: league === activeLeague }]"
          @click="handleLeagueFilter(league)"
        >
          {{ league === "all" ? $t("general.all.female") : league }}<br />
          <span v-if="league === 'brazil'" class="text-xs flex items-center justify-center mt-1 lowercase"
            >by
            <img
              src="/img/mercato/visa-brand.png"
              alt="Visa"
              width="50"
              height="16"
              class="max-h-3 w-auto ms-1"
          /></span>
        </button>
      </div>
    </div>

    <div v-if="!isSimulatorOpen">
      <div class="border-b border-b-mercato">
        <div class="kql-w-container flex items-stretch justify-center">
          <button
            type="button"
            :class="['mercato-tab', { active: activeTab === 'market' }]"
            @click="activeTab = 'market'"
          >
            {{ $t("mercato.playerMarket") }}
          </button>
          <button
            type="button"
            :class="['mercato-tab', { active: activeTab === 'teams' }]"
            @click="activeTab = 'teams'"
          >
            {{ $t("menuItem.teams") }}
          </button>
          <button
            type="button"
            :class="['mercato-tab', { active: activeTab === 'transfers' }]"
            @click="activeTab = 'transfers'"
          >
            {{ $t("mercato.transfers") }}
          </button>
        </div>
      </div>

      <client-only>
        <market-section
          v-show="activeTab === 'market'"
          :teams="kingsTeams || []"
          :league="activeLeague"
          :market-players="marketPlayers || []"
          :market-players-status="marketPlayersStatus"
        ></market-section>
        <teams-section v-show="activeTab === 'teams'" :league="activeLeague" :teams="kingsTeams || []">
        </teams-section>
        <transfers-section
          v-show="activeTab === 'transfers'"
          :league="activeLeague"
          :teams="kingsTeams || []"
        ></transfers-section>
      </client-only>
    </div>

    <!-- Mercato Simulator Modal -->

    <lazy-pages-mercato-global-mercato-simulator
      v-if="kingsTeams"
      v-model="isSimulatorOpen"
      :teams="kingsTeams"
      :league="activeLeague"
      :market-players="marketPlayers"
    />

    <div id="generalInfo">
      <div class="bg-gradient-to-b from-primary-dark to-black">
        <section class="!mt-0 pt-16">
          <h2>{{ $t("mercato.keyDates.title", { year: "2026" }) }}</h2>
          <div class="flex flex-col gap-0 sm:flex-row items-center justify-start sm:justify-stretch mt-10">
            <div
              v-for="(image, i) in windows"
              class="flex flex-col items-center flex-shrink relative sm:flex-row sm:justify-center sm:flex-grow"
            >
              <nuxt-img
                :src="image.src"
                :alt="image.alt"
                width="500"
                height="383"
                sizes="250px md:400px"
                class="block sm:w-[calc(100%-24px)] sm:flex-grow"
              ></nuxt-img>
              <svg
                v-if="i < windows.length - 1"
                width="269"
                height="148"
                viewBox="0 0 269 148"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                class="w-8 sm:w-6 sm:flex-shrink rotate-90 sm:rotate-0 h-8 sm:h-auto sm:rtl:-scale-x-100"
              >
                <path
                  d="M266.071 80.7107C269.976 76.8055 269.976 70.4738 266.071 66.5686L202.431 2.92897C198.526 -0.976273 192.195 -0.976273 188.289 2.92897C184.384 6.83421 184.384 13.1659 188.289 17.0711L244.858 73.6396L188.289 130.208C184.384 134.113 184.384 140.445 188.289 144.35C192.195 148.256 198.526 148.256 202.431 144.35L266.071 80.7107ZM0 73.6396V83.6396H259V73.6396V63.6396H0V73.6396Z"
                  fill="#01C94B"
                />
              </svg>
            </div>
          </div>
        </section>
      </div>

      <section id="mechanicsVideo" class="!mt-0">
        <h2>{{ $t("mercato.mechanics.title") }}</h2>
        <div class="flex justify-center mt-6">
          <iframe
            loading="lazy"
            width="100%"
            height="auto"
            class="max-w-[1153px] focus-visible:outline-none focus-visible:border-none aspect-video"
            :src="`https://www.youtube-nocookie.com/embed/${videoLangMapping[locale]}?rel=0`"
            :title="$t('mercato.mechanics.title')"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowfullscreen
          ></iframe>
        </div>
      </section>

      <section id="simulatorBanner" class="px-2">
        <div class="bg-gray-800 rounded-lg py-10 px-4">
          <h2>{{ $t("mercato.simulator.banner.title") }}</h2>
          <p class="subtitle">
            {{ $t("mercato.simulator.banner.subtitle") }}
          </p>

          <div class="mt-10 btn btn-emphasis max-w-max mx-auto text-center">
            <button type="button" @click="openSimulator">{{ $t("mercatoSimulator.title") }}</button>
          </div>
        </div>
      </section>
      <mercato-faqs />

      <section class="partner" v-if="activeLeague === 'brazil'">
        <p class="kql-page-section-title text-center">Official Mercato Sponsor</p>
        <hr />
        <div class="flex items-center justify-center">
          <img
            src="/img/mercato/visa-brand-footer.png"
            alt="Visa"
            width="450"
            height="146"
            class="w-36 mt-6"
          />
        </div>
      </section>
    </div>
  </main>
</template>

<style scoped>
.league-filter--button {
  @apply px-1 py-2 sm:px-3 rounded-md text-xs sm:text-sm md:text-base md:leading-none bg-gray-800 border border-gray-600 hover:bg-gray-600 uppercase leading-none;
}

.league-filter--button.active {
  @apply bg-mercato text-black border-mercato;
}

.kql-mercato p.subtitle {
  @apply !my-1 text-center text-gray-400 text-lg;
}

.kql-mercato h2,
.kql-mercato h3,
.kql-mercato h4 {
  @apply text-center;
}

.kql-mercato section {
  @apply mb-14 mt-16 md:mb-24;
}

.kql-mercato section > div {
  @apply max-w-7xl mx-auto px-4 md:px-0;
}

.mercato-tab {
  @apply uppercase text-sm bigsm:text-base bigsm:leading-none flex items-center justify-center leading-none py-4 px-4 flex-1 sm:flex-none transition-all duration-150 ease-in-out;
}

.mercato-tab.active {
  @apply border-b-4 border-mercato !pb-3 text-mercato;
}

.kql-mercato .header {
  background-image:
    linear-gradient(0deg, rgba(0, 0, 0, 0.8) 20%, transparent 100%),
    url("/img/mercato/bg-title-mercato-page.jpg");
  background-size: cover;
}

.kql-mercato :deep(.btn-primary) {
  @apply !border-mercato !text-mercato hover:!bg-mercato hover:!text-black;
}

.kql-mercato :deep(.btn-emphasis) {
  @apply !bg-mercato !border-mercato hover:!bg-transparent hover:!text-mercato hover:!border-mercato;
}
</style>
